import { SupportedLanguages } from '@/plugins/i18n';
import { getFileBuffer } from '@/vendor/pleased-sdk/util';
import Axios, { AxiosResponse } from 'axios';
import * as ApiModel from './models.pleased';

const httpClient = Axios.create({
  headers: {
    'pl-access-token': '',
    'brand-subdomain': '',
    'language': 'en',
  },
});

const SUCCESS_CODE = 10000;

export interface PleaseApiConfig {
  url?: string;
  brandCode?: string;
  token?: string;
  acceptLanguage?: SupportedLanguages | '*' | '';
}

/**
 * Conditionally set the none empty configurations.
 */
export function setConfig ({ url = '', brandCode = '', token = '', acceptLanguage = '' }: PleaseApiConfig) {
  url && (httpClient.defaults.baseURL = url);
  brandCode && (httpClient.defaults.headers['brand-subdomain'] = brandCode);
  token && (httpClient.defaults.headers['pl-access-token'] = token);
  acceptLanguage && (httpClient.defaults.headers['language'] = acceptLanguage);
}

/**
 * Get a list of categories to show when the chatbot starts.
 * @returns The list of categories to show.
 */
export async function getBrandConfig ({ brandCode }: { brandCode?: string } = {}) {
  brandCode = brandCode || httpClient.defaults.headers['brand-subdomain'];
  const response = await httpClient.get<ApiModel.BrandConfigResponse>(`/api/v1/chat-widget/brand/${brandCode}`);
  return response.data;
}

/**
 * Get sections/details of the given category.
 */
export async function getCategoryDetails ({ categoryId }: { categoryId: string|number }) {
  const response = await httpClient.get<ApiModel.CategoryDetailResponse>(`/api/v1/chat-widget/category/${categoryId}`);
  throwIfError(response);
  response.data.data.noneOfTheAboveConfig = response.data.data.noneOfTheAboveConfig || {
    noneOfTheAbove: false,
  };
  return response.data;
}
getCategoryDetails.cached = createCache(getCategoryDetails, ({ categoryId }) => localizeKey(categoryId));

/**
 * Get the subsections/articles in the given section.
 */
export async function getSectionDetails ({ sectionId }: { sectionId: string|number }) {
  const response = await httpClient.get<ApiModel.SectionDetailResponse>(`/api/v1/chat-widget/section/${sectionId}`);
  return response.data;
}
getSectionDetails.cached = createCache(getSectionDetails, ({ sectionId }) => localizeKey(sectionId));

/**
 * Get the subsections/articles in the given section.
 */
export async function getArticleDetails ({ articleId }: { articleId: string|number }) {
  const response = await httpClient.get<ApiModel.ArticleDetailResponse>(`/api/v1/chat-widget/article/${articleId}`);
  throwIfError(response);
  return response.data;
}
getArticleDetails.cached = createCache(getArticleDetails, ({ articleId }) => localizeKey(articleId));

/**
 * Create a ticket with the given fields.
 * @param request The request body for the ticket.
 */
export async function createTicket (request: ApiModel.TicketCreateRequest) {
  const response = await httpClient.post<ApiModel.TicketCreateResponse>('/api/v1/support/tickets/open-request', request);
  throwIfError(response);
  return response.data.data;
}

/**
 * Get a list of all ticket fields
 */
export async function getTicketFields () {
  const response = await httpClient.get<ApiModel.TicketFieldsResponse>('/api/v1/support/ticket-fields/non-system-user-editable');
  throwIfError(response);
  return response.data.data;
}

getTicketFields.cached = createCache(getTicketFields);

/**
 * Get the position of the queue for the client.
 */
export async function getQueuePosition () {
  const response = await httpClient.get<ApiModel.QueuePositionResponse>('/api/v1/chat/client-queue-position');
  throwIfError(response);
  return response.data.data;
}

export async function getPleasedTokenFromSportyToken ({ countryCode, sportyToken }: {countryCode: string, sportyToken: string}) {
  const response = await httpClient.get<ApiModel.UserInformationResponse>(`/api/v1/support/users/sportybet-user-token/${countryCode}`, { headers: { 'sf-access-token': sportyToken } });
  throwIfError(response);
  return response.data.data;
}

/**
 * Get a user token for guest user to perform call/chat activities.
 */
export async function getAnonymousToken () {
  const response = await httpClient.get<ApiModel.UserInformationResponse>('/api/v1/support/users/anonymous-token');
  throwIfError(response);
  return response.data.data;
}

export async function listTickets (params: ApiModel.TicketListQueryParams) {
  const response = await httpClient.get<ApiModel.TicketListResponse>('/api/v1/support/tickets', { params: { ...params, currentBrandOnly: true } });
  throwIfError(response);
  return response.data;
}

export async function getTicket (ticketId: number) {
  const response = await httpClient.get<ApiModel.TicketCreateResponse>(`/api/v1/support/tickets/${ticketId}`);
  throwIfError(response);
  return response.data.data;
}

export async function getTicketComments (ticketId: number) {
  const response = await httpClient.get<ApiModel.ApiResponse<ApiModel.Comment[]>>(`/api/v1/support/tickets/${ticketId}/comments`);
  throwIfError(response);
  return response.data;
}

export async function addTicketComment ({ ticketId, comment, attachment = 0, sourceId = 1 }: { ticketId: number, comment: string, attachment?: number, sourceId?: number }) {
  const response = await httpClient.post<ApiModel.ApiResponse<ApiModel.Comment>>(`/api/v1/support/tickets/${ticketId}/comment`, {
    comment,
    isPublic: true,
    attachmentIds: attachment ? [attachment] : [],
    sourceId,
  });
  throwIfError(response);
  return response.data.data;
}

export async function addAttachmentComment ({ ticketId, file }: { ticketId: number, file: File }) {
  const fileBuffer = await getFileBuffer(file);
  const encodedFileName = btoa(file.name);
  const headerParams = {
    'file-name': encodedFileName || 'unknown',
    'is-inline': false,
    'Content-Type': 'application/octet-stream',
    'base64-file-name': encodedFileName,
  };
  const attachmentResponse = await httpClient.post<ApiModel.AddAttachmentResponse>('/api/v1/attachment/ticket_comment', fileBuffer, { headers: headerParams });
  throwIfError(attachmentResponse);

  const commentResponse = await addTicketComment({ ticketId, comment: 'File Upload', attachment: attachmentResponse.data.data.id });
  return commentResponse;
}

/**
 * Check if agents are online for voice chat.
 */
export async function areVoiceChatAgentsOnline () {
  try {
    const response = await httpClient.get<ApiModel.AgentsOnlineResponse>('/api/v1/voice-chat/online-agents');
    return response.data.data.onlineAgents;
  } catch {}
  return false;
}

export async function getActiveConversations ({ onlyActive = true, limit = 5 }:{onlyActive?: boolean, limit?: number} = {}) {
  const response = await httpClient.get<ApiModel.ActiveTicketResponse>('/api/v1/support/tickets/self', { params: { onlyActive } });
  throwIfError(response);
  let { data: tickets, total } = response.data;
  tickets = tickets.sort((a, b) => (new Date(b.updatedAt).valueOf()) - (new Date(a.updatedAt).valueOf()));
  return { tickets: tickets.length > limit ? tickets.slice(0, limit) : tickets, total: tickets.length };
}

export async function getActiveConversationComments (ticketId: number) {
  const response = await httpClient.get<ApiModel.ActiveTicketCommentsResponse>(`/api/v1/support/tickets/${ticketId}/conversations`);
  throwIfError(response);
  return response.data;
}

export async function matchOverlayTrigger ({ type, id }: { type: 'CATEGORY' | 'SECTION' | 'ARTICLE', id: number }) {
  const response = await httpClient.post<ApiModel.ChatOverlayTriggerResponse>('/api/v1/chat/chat-triggers/trigger', { type, id });
  throwIfError(response);
  return response?.data?.data?.message ? response?.data?.data : null;
}

matchOverlayTrigger.cached = createCache(matchOverlayTrigger, ({ type, id }) => localizeKey(`${type}-${id}`));

export async function trackOverlyClick (payload: {type: 'END_CHAT' | 'CONTINUE', chatTriggerId: number|string}) {
  await httpClient.post('/api/v1/chat/chat-triggers/trigger/track', payload).catch(() => {});
}

/**
 * Utility that throws error if an API error has occurred.
 * @param response The response from API
 */
function throwIfError (response: AxiosResponse<ApiModel.ApiResponse<unknown>>) {
  if (response.data.code !== SUCCESS_CODE) {
    // eslint-disable-next-line no-console
    console.error('API Error:', response.data.message, 'URL', response.config.url);
    throw new Error(response.data.message);
  }
}

/**
 * Helper function to add locale information to the cache.
 * @param key The key to localize
 */
function localizeKey (key: string|number) {
  return `${key}--lang:${httpClient.defaults.headers['language'] || 'en'}`;
}

// #region Utilities
type Awaited<T> = T extends PromiseLike<infer U>
  ? { 0: Awaited<U>; 1: U }[U extends PromiseLike<any> ? 0 : 1]
  : T

/**
 * Invalidate the cache for the given arguments. If no argument is given, then
 * the entire cache is invalidated.
 */
interface InvalidateCache<P extends any[]> {
  /**
   * Invalidate the cache for the given arguments.
   */
  (...args: P): boolean
  /**
   * Invalidate entire cache.
   */
  (): boolean
}

/**
 * A cached version of the callable. The returned objects are cached for further usage.
 * This utility does not cache errors. If errors are needed to be cached, the error needs to be handled in `callable` and returned
 * as a successful response.
 * @see createCache
 */
interface CachedCallable<T extends (...args: any[]) => Promise<any>> {
  /**
   * A cached version of the callable. The returned objects are cached for further usage.
   * This utility does not cache errors. If errors are needed to be cached, the error needs to be handled in `callable` and returned
   * as a successful response.
   * @see createCache
   */
  (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>>;
  /**
   * Invalidate the cache for the given arguments. If no argument is given, then
   * the entire cache is invalidated.
   */
  invalidate: InvalidateCache<Parameters<T>>;
}

/**
 * Create a cached version of the given api. The returned objects are cached for further usage.
 * This utility does not cache errors. If errors are needed to be cached, the error needs to be handled in `callable` and returned
 * as a successful response.
 *
 * NOTE: Modifying the result object will also modify the entry in the cache as the results are shallow copy of the objects returned from
 * the `callable`.
 *
 * NOTE: The tool caches the results of the promise rather than the promise itself.
 * @param callable The callable to create cache for.
 * @param key A function to generate the key from the arguments of the callable. If nothing is passed here, then the results are cached.
 * @returns A promise that resolves with the cached results
 */
function createCache<T extends (...args:any[]) => Promise<any>>(callable: T, key?: (...args: Parameters<T>) => string|number): CachedCallable<T> {
  const createKey = key || (() => 'single');
  let memoryStore: Map<string|number, Awaited<ReturnType<T>>> = new Map();
  const cachedCallable = async (...args: Parameters<T>) => {
    const cacheKey = createKey(...args);
    if (memoryStore.has(cacheKey)) {
      return memoryStore.get(cacheKey) as Awaited<ReturnType<T>>;
    }
    try {
      const result: Awaited<ReturnType<T>> = await callable(...args);
      memoryStore.set(cacheKey, result);
      return result;
    } catch (error) {
      throw error;
    }
  };
  function invalidate(): boolean;
  function invalidate(...args: Parameters<T>): boolean;
  function invalidate (...args: Parameters<T>) {
    if (!args.length) {
      memoryStore.clear();
      return true;
    }
    return memoryStore.delete(createKey(...args));
  }
  /**
   * Invalidate cache for the given arguments.
   */
  cachedCallable.invalidate = invalidate;

  return cachedCallable;
}
// #endregion
